const SDK_URL = 'https://res.cdn.office.net/teams-js/2.47.0/js/MicrosoftTeams.min.js';
const SDK_INTEGRITY = 'sha384-IkfULjDxwgKWYEYHErhDcuw+ahOQvmrcRFwgI3sm/uu7c0HWVjyYG3qp5z5SncxD';
const INIT_TIMEOUT_MS = 2500;

type Heuristic = false | true | 'maybe';
type State = 'unknown' | 'no' | 'yes';

let state: State = 'unknown';
let initPromise: Promise<boolean> | null = null;
let sdkPromise: Promise<any> | null = null;

function mightBeInTeams(): Heuristic {
    if (window.parent === window.self) return false;

    const name = window.name || '';
    if (name.includes('embedded-page-container') || name.includes('extension-tab-frame')) {
        return true;
    }

    const ancestors = window.location.ancestorOrigins;
    if (ancestors) {
        for (const origin of Array.from(ancestors)) {
            try {
                if (/teams\.(microsoft|live)\.com$/.test(new URL(origin).hostname)) {
                    return true;
                }
            }
            catch {
                // ignore malformed origin entries
            }
        }
    }

    if (/teams\.(microsoft|live)\.com/.test(document.referrer)) return true;

    return 'maybe';
}

function loadSdk(): Promise<any> {
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise((resolve, reject) => {
        const existing = (window as any).microsoftTeams;
        if (existing) {
            resolve(existing);
            return;
        }

        const script = document.createElement('script');
        script.src = SDK_URL;
        script.integrity = SDK_INTEGRITY;
        script.crossOrigin = 'anonymous';
        script.async = true;
        script.onload = () => {
            const sdk = (window as any).microsoftTeams;
            if (sdk) resolve(sdk);
            else reject(new Error('Teams SDK loaded but window.microsoftTeams is missing'));
        };
        script.onerror = () => reject(new Error('Failed to load Microsoft Teams SDK'));
        document.head.appendChild(script);
    });

    return sdkPromise;
}

export const MsTeams = {
    isActive(): boolean {
        return state === 'yes';
    },

    init(): Promise<boolean> {
        if (initPromise) return initPromise;

        initPromise = (async () => {
            const heuristic = mightBeInTeams();
            if (heuristic === false) {
                state = 'no';
                return false;
            }

            try {
                const sdk = await loadSdk();
                const timeout = new Promise<never>((_, reject) => {
                    setTimeout(() => reject(new Error('Teams app.initialize() timed out')), INIT_TIMEOUT_MS);
                });
                await Promise.race([
                    timeout,
                    sdk.app.initialize()
                ]);

                await sdk.app.getContext();
                await sdk.app.notifySuccess();

                state = 'yes';
                return true;
            }
            catch {
                state = 'no';
                return false;
            }
        })();

        return initPromise;
    }
};
